/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { defineMessage, useIntl } from 'react-intl';

import { LinkButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import Lozenge from '@atlaskit/lozenge';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const labelMessage = defineMessage({
	id: 'ai-mate.rovo-memories.manage-memory-modal.feedback-banner-label',
	defaultMessage: 'INTERNAL ONLY',
	description: 'Label for the internal-only feedback banner lozenge',
});

const textMessage = defineMessage({
	id: 'ai-mate.rovo-memories.manage-memory-modal.feedback-banner-text',
	defaultMessage: "Help us improve Rovo's memory management experience.",
	description: 'Text for the feedback banner in the manage memory modal',
});

const actionMessage = defineMessage({
	id: 'ai-mate.rovo-memories.manage-memory-modal.feedback-banner-action',
	defaultMessage: 'Share feedback',
	description: 'Label for the share feedback button in the feedback banner',
});

const styles = cssMap({
	feedbackBanner: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.150'),
		minWidth: '0px',
		maxWidth: '100%',
		boxSizing: 'border-box',
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
	},
	feedbackBannerMain: {
		minWidth: '0px',
		flex: '1 1 160px',
		maxWidth: '100%',
	},
	feedbackBannerLabelRow: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: '0px',
		maxWidth: '100%',
	},
	feedbackBannerLozenge: {
		flexShrink: 0,
	},
	feedbackBannerCopy: {
		minWidth: '0px',
		flex: '1 1 12rem',
	},
});

export type FeedbackBannerProps = {
	feedbackUrl: string;
};

export const FeedbackBanner = ({ feedbackUrl }: FeedbackBannerProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.feedbackBanner}>
			<Box xcss={styles.feedbackBannerMain}>
				<Box xcss={styles.feedbackBannerLabelRow}>
					<Box xcss={styles.feedbackBannerLozenge}>
						<Lozenge appearance="discovery">{formatMessage(labelMessage)}</Lozenge>
					</Box>
					<Box xcss={styles.feedbackBannerCopy}>
						<Text size="small">{formatMessage(textMessage)}</Text>
					</Box>
				</Box>
			</Box>
			<LinkButton href={feedbackUrl} target="_blank" appearance="default" iconBefore={FeedbackIcon}>
				{formatMessage(actionMessage)}
			</LinkButton>
		</Box>
	);
};

export default FeedbackBanner;
